// GC (Treasury yield curve) and ECST (economic statistics from FRED).

import {
  AreaSeries,
  BaselineSeries,
  HistogramSeries,
  LineSeries,
  createYieldCurveChart,
  type ISeriesApi,
  type MouseEventParams,
  type SeriesType,
  type Time,
} from "../../vendor/lwc.mjs";
import { ApiError, get, type Meta } from "../api";
import { COLORS, THEME, makeChart } from "../chart";
import { clear, errorBox, h, loading, spark, toggles } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";
import { DataTable } from "../table";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/// `YYYY-MM-DD` moved by whole months, then days. Month ends clamp, so a
/// month before Mar 31 is Feb 28.
function shift(iso: string, days: number, months = 0): string {
  const [y, m, d] = iso.split("-").map(Number);
  const monthLen = new Date(Date.UTC(y, m + months, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, Math.min(d, monthLen) + days)).toISOString().slice(0, 10);
}

const chartWrap = () => {
  const box = h("div", { class: "chartbox" });
  const legend = h("div", { class: "legend" });
  return { wrap: h("div", { class: "chartwrap" }, box, legend), box, legend };
};

// --- GC ---------------------------------------------------------------------

interface Curve {
  /// Maturities in months, ascending.
  months: number[];
  /// Oldest first; `y` parallel to `months`.
  rows: { d: string; y: (number | null)[] }[];
}

const tenor = (m: number) => (m < 12 ? `${m}M` : `${m / 12}Y`);

/// Comparison curves: label, color, and how far back from the latest date.
const COMPARE: [string, string, (d: string) => string][] = [
  ["1D", "#777", (d) => shift(d, -1)],
  ["1W", "#6fb6ff", (d) => shift(d, -7)],
  ["1M", "#ff6fd8", (d) => shift(d, 0, -1)],
  ["1Y", "#3ddc5f", (d) => shift(d, 0, -12)],
];

/// Spreads for the history chart: label, short tenor, long tenor (months).
const SPREADS: [string, number, number][] = [
  ["2s10s", 24, 120],
  ["3m10y", 3, 120],
  ["2s30s", 24, 360],
  ["5s30s", 60, 360],
];

interface TenorRow {
  i: number;
  months: number;
  y: number;
  chg: (number | undefined)[];
}

function runGC(ctx: Ctx): void {
  let curve: Curve | null = null;
  let even = true;
  const shown = new Set(["1W", "1M", "1Y"]);
  /// Lower chart: a spread label or a tenor (months) picked from the table.
  let lower: string | number = "2s10s";

  const toolbar = h("div", { class: "toolbar" });
  const top = chartWrap();
  const bottom = chartWrap();
  const lowerBar = h("div", { class: "toolbar", style: { borderTop: "1px solid var(--line)" } });
  const dates = h("div", { class: "pad dim", style: { padding: "2px 8px" } });
  const table = new DataTable<TenorRow>(
    [
      { label: "Tenor", get: (r) => tenor(r.months), cls: () => "amber" },
      { label: "Yield", get: (r) => r.y, fmt: (v) => f.fixedN(v, 2), num: true },
      ...COMPARE.map(([label], k) => ({
        label,
        get: (r: TenorRow) => r.chg[k],
        fmt: (v: number) => f.bp(v),
        cls: (v: number) => f.cls(v),
        num: true,
      })),
    ],
    { panel: ctx.panel, sortable: false, action: (r) => () => ((lower = r.months), drawToolbar(), drawLower()) },
  );
  // Charts stacked on the left, the tenor table down the right.
  const side = h("div", { style: { gridRow: "1 / 4", gridColumn: "2", overflow: "auto", borderLeft: "1px solid var(--line)" } }, dates, table.el);
  const grid = h(
    "div",
    {
      style: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gridTemplateRows: "minmax(0, 3fr) auto minmax(0, 2fr)",
        flex: "1",
        minHeight: "0",
      },
    },
    top.wrap,
    side,
    lowerBar,
    h("div", { style: { display: "flex", minHeight: "0" } }, bottom.wrap),
  );
  clear(ctx.body, h("div", { class: "fill" }, toolbar, grid));
  clear(top.box, loading());

  const drawToolbar = () => {
    clear(
      toolbar,
      h("label", null, "Compare "),
      ...COMPARE.map(([label, color]) =>
        h(
          "button",
          {
            class: shown.has(label) ? "on" : undefined,
            style: shown.has(label) ? { background: color, borderColor: color } : { color },
            onclick: () => (shown.has(label) ? shown.delete(label) : shown.add(label), drawToolbar(), drawCurve()),
          },
          label,
        ),
      ),
      h("span", { class: "sep" }),
      h("label", null, "Axis "),
      ...toggles(
        [
          ["even", "Tenor"],
          ["linear", "Maturity"],
        ] as [string, string][],
        even ? "even" : "linear",
        (v) => ((even = v === "even"), drawToolbar(), drawCurve()),
      ),
    );
    clear(
      lowerBar,
      h("label", null, "History "),
      ...toggles(
        SPREADS.map((s) => s[0]),
        typeof lower === "string" ? lower : "",
        (v) => ((lower = v), drawToolbar(), drawLower()),
      ),
      typeof lower === "number" ? h("button", { class: "on" }, `${tenor(lower)} yield`) : h("span", { class: "dim" }, " or pick a tenor"),
    );
  };

  /// The last row on or before `iso` (rows are oldest first).
  const onOrBefore = (iso: string) => {
    const rows = curve!.rows;
    for (let i = rows.length - 1; i >= 0; i--) if (rows[i].d <= iso) return rows[i];
    return undefined;
  };

  // Each redraw replaces the chart; unmounting removes the current one.
  let curveAbort: AbortController | null = null;
  let lowerAbort: AbortController | null = null;
  ctx.signal.addEventListener("abort", () => (curveAbort?.abort(), lowerAbort?.abort()));
  const drawCurve = () => {
    if (!curve) return;
    curveAbort?.abort();
    curveAbort = new AbortController();
    const signal = curveAbort.signal;
    clear(top.box);
    const rows = curve.rows;
    const last = rows[rows.length - 1];
    const months = curve.months;
    // Tenor axis: evenly spaced points labelled by tenor; otherwise months.
    const x = (i: number) => (even ? i : months[i]);
    const c = createYieldCurveChart(top.box, {
      autoSize: true,
      layout: THEME.layout,
      grid: THEME.grid,
      crosshair: THEME.crosshair,
      leftPriceScale: { visible: false },
      rightPriceScale: { visible: true, borderColor: "#2e2e2e" },
      // Room on the right so the 30Y point and its label aren't clipped.
      timeScale: { borderColor: "#2e2e2e", rightOffset: even ? 0.5 : 12 },
      localization: {
        priceFormatter: (v: number) => `${v.toFixed(2)}%`,
        // The default labels months as "2Y6M"; on the tenor axis x is an index.
        timeFormatter: even ? (i: number) => (months[i] ? tenor(months[i]) : "") : undefined,
      },
      yieldCurve: { baseResolution: 1, minimumTimeRange: even ? months.length - 1 : 360, startTimeRange: 0 },
    });
    signal.addEventListener("abort", () => c.remove());

    const curves: { label: string; color: string; y: (number | null)[] }[] = [];
    const add = (label: string, color: string, row: Curve["rows"][number], width: 1 | 2 | 3) => {
      const s = c.addSeries(LineSeries, { color, lineWidth: width, pointMarkersVisible: true, pointMarkersRadius: width + 1 });
      s.setData(row.y.flatMap((v, i) => (v === null ? [] : [{ time: x(i), value: v }])));
      curves.unshift({ label, color, y: row.y });
    };
    COMPARE.forEach(([label, color, back]) => {
      const row = label === "1D" ? rows[rows.length - 2] : onOrBefore(back(last.d));
      if (shown.has(label) && row) add(label, color, row, 1);
    });
    add("Last", COLORS.amber, last, 2);
    c.timeScale().fitContent();

    const legend = (i?: number) =>
      clear(
        top.legend,
        i === undefined ? null : h("span", { class: "amber" }, tenor(months[i])),
        ...curves.map((k) => h("span", { style: { color: k.color } }, k.label, i === undefined ? "" : ` ${f.fixedN(k.y[i], 2)}`)),
      );
    legend();
    c.subscribeCrosshairMove((p: MouseEventParams<number>) => {
      const i = p.time === undefined ? -1 : even ? p.time : months.indexOf(p.time);
      legend(i >= 0 && i < months.length ? i : undefined);
    });
  };

  const drawLower = () => {
    if (!curve) return;
    lowerAbort?.abort();
    lowerAbort = new AbortController();
    clear(bottom.box);
    const { months, rows } = curve;
    let name: string;
    let points: { time: Time; value: number }[];
    if (typeof lower === "number") {
      const i = months.indexOf(lower);
      name = `${tenor(lower)} yield`;
      points = rows.flatMap((r) => (r.y[i] == null ? [] : [{ time: r.d as Time, value: r.y[i]! }]));
    } else {
      const [label, a, b] = SPREADS.find((s) => s[0] === lower)!;
      const [ia, ib] = [months.indexOf(a), months.indexOf(b)];
      name = `${label} spread, bp`;
      points = rows.flatMap((r) =>
        r.y[ia] == null || r.y[ib] == null ? [] : [{ time: r.d as Time, value: Math.round((r.y[ib]! - r.y[ia]!) * 100) }],
      );
    }
    const c = makeChart(bottom.box, lowerAbort.signal, { timeScale: { timeVisible: false, borderColor: "#2e2e2e" } });
    let s: ISeriesApi<SeriesType>;
    if (typeof lower === "number") {
      s = c.addSeries(LineSeries, { color: COLORS.amber, lineWidth: 2, priceFormat: { type: "price", precision: 2, minMove: 0.01 } });
    } else {
      s = c.addSeries(BaselineSeries, {
        baseValue: { type: "price", price: 0 },
        topLineColor: COLORS.up,
        topFillColor1: "rgba(61,220,95,0.25)",
        topFillColor2: "rgba(61,220,95,0.02)",
        bottomLineColor: COLORS.down,
        bottomFillColor1: "rgba(255,75,75,0.02)",
        bottomFillColor2: "rgba(255,75,75,0.25)",
        lineWidth: 2,
        priceFormat: { type: "price", precision: 0, minMove: 1 },
      });
    }
    s.setData(points);
    c.timeScale().fitContent();
    const unit = typeof lower === "number" ? "%" : "bp";
    const show = (k: number) => {
      const p = points[k];
      if (!p) return clear(bottom.legend);
      const ch = p.value - points[0].value;
      clear(
        bottom.legend,
        h("span", { class: "amber" }, name),
        h("span", null, String(p.time)),
        h("span", null, `${f.fixedN(p.value, unit === "%" ? 2 : 0)}${unit}`),
        h("span", { class: f.cls(ch) }, `${ch > 0 ? "+" : ""}${f.fixedN(ch, unit === "%" ? 2 : 0)}${unit} since ${points[0].time}`),
      );
    };
    show(points.length - 1);
    const index = new Map<Time, number>(points.map((p, k) => [p.time, k]));
    c.subscribeCrosshairMove((p: MouseEventParams) => show((p.time !== undefined ? index.get(p.time) : undefined) ?? points.length - 1));
  };

  const fillTable = () => {
    const rows = curve!.rows;
    const last = rows[rows.length - 1];
    const cmp = COMPARE.map(([label, , back]) => (label === "1D" ? rows[rows.length - 2] : onOrBefore(back(last.d))));
    clear(
      dates,
      `${last.d} vs `,
      ...COMPARE.flatMap(([label], k) => (cmp[k] ? [h("span", { class: "amber" }, label), ` ${cmp[k]!.d.slice(2)} `] : [])),
    );
    table.set(
      curve!.months.flatMap((months, i) => {
        const y = last.y[i];
        if (y === null) return [];
        return [{ i, months, y, chg: cmp.map((r) => (r && r.y[i] !== null ? y - r.y[i]! : undefined)) }];
      }),
    );
  };

  const load = async () => {
    try {
      const r = await get<Curve>("treasury/curve", {}, ctx.signal);
      if (!r.data.rows.length) throw new Error("No yield curve data");
      curve = r.data;
      ctx.panel.status(`US Treasury daily par yield curve · ${curve.rows[curve.rows.length - 1].d}`, r.meta);
      fillTable();
      drawCurve();
      drawLower();
    } catch (e) {
      if (!ctx.signal.aborted && !curve) clear(top.box, errorBox(e));
    }
  };
  drawToolbar();
  load();
  ctx.panel.every(30 * 60_000, load);
}

// --- ECST -------------------------------------------------------------------

interface SeriesMeta {
  id: string;
  name: string;
  /// level, yoy (% vs a year ago), diff (change vs prior period), pop (% vs prior period).
  tf: "level" | "yoy" | "diff" | "pop";
  unit: string;
  dp: number;
  freq: "D" | "W" | "M" | "Q";
}
interface Latest extends SeriesMeta {
  date: string | null;
  value: number | null;
  prevDate: string | null;
  prev: number | null;
  yearAgo: number | null;
  spark: number[];
}
interface History extends SeriesMeta {
  d: string[];
  v: number[];
}

/// Server-side group keys, in display order.
const GROUPS = ["growth", "labour", "inflation", "rates", "housing", "sentiment", "money"];
const UNITS: Record<string, [string, string]> = { "%": ["", "%"], bp: ["", "bp"], K: ["", "K"], M: ["", "M"], $T: ["$", "T"] };
const TF_NOTE: Record<SeriesMeta["tf"], string> = {
  level: "",
  yoy: "% change from a year earlier",
  diff: "change from the prior period",
  pop: "% change from the prior period",
};

/// A value the way the series is quoted: "4.2%", "+29K", "$6.76T", "35bp".
function quoted(m: SeriesMeta, v: unknown, signed = m.tf === "diff" || m.tf === "pop", unit = true): string {
  const n = f.num(v);
  if (n === undefined) return f.NA;
  const [pre, suf] = unit ? (UNITS[m.unit] ?? ["", ""]) : ["", ""];
  const sign = n < 0 ? "-" : signed && n > 0 ? "+" : "";
  return sign + pre + f.fixedN(Math.abs(n), m.dp) + suf;
}

function period(m: SeriesMeta, d: string | null): string {
  if (!d) return f.NA;
  const [y, mo] = d.split("-");
  if (m.freq === "M") return `${MONTHS[+mo - 1]} ${y}`;
  if (m.freq === "Q") return `${y} Q${Math.ceil(+mo / 3)}`;
  return d;
}

type EcstRow = { group: string } | { s: Latest };

function runECST(ctx: Ctx): void {
  const id = ctx.args.find((a) => !RANGE_ARG.test(a));
  if (id) return runSeries(ctx, id, ctx.args.find((a) => RANGE_ARG.test(a)) ?? "10Y");

  const loaded = new Map<string, { title: string; rows: Latest[]; meta: Meta } | Error>();
  const table = new DataTable<EcstRow>(
    [
      { label: "Ticker", get: (r) => ("s" in r ? r.s.id : ""), cls: () => "amber" },
      { label: "Series", get: (r) => ("s" in r ? r.s.name : ""), cls: () => "name" },
      { label: "Period", get: (r) => ("s" in r ? period(r.s, r.s.date) : "") },
      { label: "Last", get: (r) => ("s" in r ? r.s.value : null), fmt: (v, r) => ("s" in r ? quoted(r.s, v) : ""), num: true },
      { label: "Prior", get: (r) => ("s" in r ? r.s.prev : null), fmt: (v, r) => ("s" in r ? quoted(r.s, v) : ""), cls: () => "dim", num: true },
      {
        label: "Chg",
        get: (r) => ("s" in r && r.s.value !== null && r.s.prev !== null ? r.s.value - r.s.prev : null),
        fmt: (v, r) => ("s" in r ? quoted(r.s, v, true, r.s.unit !== "%") : ""),
        cls: (v) => f.cls(v),
        num: true,
        title: "Change from the prior period; percentage points for rates",
      },
      { label: "1Y ago", get: (r) => ("s" in r ? r.s.yearAgo : null), fmt: (v, r) => ("s" in r ? quoted(r.s, v) : ""), cls: () => "dim", num: true },
      { label: "2Y trend", get: () => 0, fmt: (_v, r) => ("s" in r && r.s.spark.length > 1 ? spark(r.s.spark, 90, 16) : "") },
    ],
    {
      panel: ctx.panel,
      group: (r) => ("group" in r ? r.group : null),
      action: (r) => ("s" in r ? `ECST ${r.s.id}` : null),
    },
  );
  const note = h(
    "div",
    { class: "pad dim", style: { padding: "2px 8px" } },
    "Headline US data from FRED, quoted the usual way (CPI as YoY, payrolls as monthly change). Pick a row for its history.",
  );
  clear(ctx.body, note, table.el);

  const render = () => {
    const rows: EcstRow[] = [];
    for (const g of GROUPS) {
      const x = loaded.get(g);
      const name = g[0].toUpperCase() + g.slice(1);
      if (!x) rows.push({ group: `${name} …` });
      else if (x instanceof Error) rows.push({ group: `${name} — ${x.message}` });
      else rows.push({ group: x.title }, ...x.rows.map((s) => ({ s })));
    }
    table.set(rows);
    const ok = [...loaded.values()].filter((x) => !(x instanceof Error)) as { rows: Latest[]; meta: Meta }[];
    if (ok.length) {
      const asof = Math.min(...ok.map((x) => x.meta.asof));
      const n = ok.reduce((a, x) => a + x.rows.length, 0);
      ctx.panel.status(`FRED, St. Louis Fed · ${n} series`, { asof, stale: ok.some((x) => x.meta.stale) });
    }
  };

  // One group at a time: on a cold server each group fetches its series
  // from FRED within a small shared rate budget, so rows fill in group by
  // group, and a group that finds the budget busy (503) is retried.
  const load = async () => {
    for (const g of GROUPS) {
      for (let attempt = 0; ; attempt++) {
        try {
          const r = await get<{ title: string; rows: Latest[] }>("fred/group", { g }, ctx.signal);
          loaded.set(g, { ...r.data, meta: r.meta });
        } catch (e) {
          if (ctx.signal.aborted) return;
          if (e instanceof ApiError && e.status === 503 && attempt < 3) {
            await new Promise((ok) => setTimeout(ok, 2000 * (attempt + 1)));
            continue;
          }
          if (!loaded.has(g) || loaded.get(g) instanceof Error) loaded.set(g, e instanceof Error ? e : new Error(String(e)));
        }
        break;
      }
      render();
    }
  };
  render();
  load();
  ctx.panel.every(30 * 60_000, load);
}

const RANGES: [string, number][] = [
  ["1Y", 1],
  ["2Y", 2],
  ["5Y", 5],
  ["10Y", 10],
  ["20Y", 20],
  ["MAX", 0],
];
const RANGE_ARG = /^(1Y|2Y|5Y|10Y|20Y|MAX)$/;

function runSeries(ctx: Ctx, id: string, range: string): void {
  const toolbar = h("div", { class: "toolbar" });
  const head = h("div", { class: "pad" }, loading());
  const { wrap, box, legend } = chartWrap();
  clear(ctx.body, h("div", { class: "fill" }, toolbar, head, wrap));
  let data: History | null = null;
  let chartAbort: AbortController | null = null;
  ctx.signal.addEventListener("abort", () => chartAbort?.abort());

  const drawToolbar = () =>
    clear(
      toolbar,
      ...toggles(
        RANGES.map((r) => r[0]),
        range,
        (v) => ((range = v), drawToolbar(), zoom()),
      ),
      h("span", { class: "sep" }),
      h("button", { onclick: () => ctx.panel.go("ECST"), title: "Back to the table (Backspace)" }, "All series"),
    );

  let zoom = () => {};
  const draw = (d: History) => {
    chartAbort?.abort();
    chartAbort = new AbortController();
    clear(box);
    const last = d.v.length - 1;
    const yearBack = shift(d.d[last], 0, -12);
    const ya = d.d.findLastIndex((t) => t <= yearBack);
    const stat = (label: string, k: number) =>
      k >= 0 ? [h("span", { class: "amber" }, label), ` ${quoted(d, d.v[k])} `, h("span", { class: "dim" }, period(d, d.d[k])), "   "] : [];
    clear(
      head,
      h("div", null, h("span", { class: "yellow" }, d.name), "  ", h("span", { class: "dim" }, `${d.id}${d.tf === "level" ? "" : " · " + TF_NOTE[d.tf]}`)),
      h("div", null, ...stat("Last", last), ...stat("Prior", last - 1), ...stat("Year ago", ya), h("span", { class: "dim" }, `history since ${period(d, d.d[0])}`)),
    );
    // Long daily histories need bars thinner than the default minimum to fit.
    const c = makeChart(box, chartAbort.signal, { timeScale: { timeVisible: false, borderColor: "#2e2e2e", minBarSpacing: 0.001 } });
    const priceFormat = { type: "price" as const, precision: d.dp, minMove: 10 ** -d.dp };
    const points = d.d.map((t, k) => ({ time: t as Time, value: d.v[k] }));
    let s: ISeriesApi<SeriesType>;
    if (d.tf === "diff" || d.tf === "pop") {
      s = c.addSeries(HistogramSeries, { priceFormat, priceLineVisible: false });
      s.setData(points.map((p) => ({ ...p, color: p.value >= 0 ? "rgba(61,220,95,0.75)" : "rgba(255,75,75,0.75)" })));
    } else if (d.v.some((v) => v < 0)) {
      s = c.addSeries(BaselineSeries, {
        baseValue: { type: "price", price: 0 },
        topLineColor: COLORS.up,
        topFillColor1: "rgba(61,220,95,0.25)",
        topFillColor2: "rgba(61,220,95,0.02)",
        bottomLineColor: COLORS.down,
        bottomFillColor1: "rgba(255,75,75,0.02)",
        bottomFillColor2: "rgba(255,75,75,0.25)",
        priceFormat,
      });
      s.setData(points);
    } else {
      s = c.addSeries(AreaSeries, { lineColor: COLORS.amber, topColor: "rgba(255,160,40,0.3)", bottomColor: "rgba(255,160,40,0.02)", lineWidth: 2, priceFormat });
      s.setData(points);
    }
    zoom = () => {
      const years = RANGES.find((r) => r[0] === range)?.[1] ?? 0;
      const from = shift(d.d[last], 0, -12 * years);
      if (!years || from <= d.d[0]) c.timeScale().fitContent();
      else c.timeScale().setVisibleRange({ from: from as Time, to: d.d[last] as Time });
    };
    zoom();
    const show = (k: number) =>
      clear(legend, h("span", { class: "amber" }, period(d, d.d[k])), h("span", null, quoted(d, d.v[k])));
    show(last);
    const index = new Map<Time, number>(d.d.map((t, k) => [t as Time, k]));
    c.subscribeCrosshairMove((p: MouseEventParams) => show((p.time !== undefined ? index.get(p.time) : undefined) ?? last));
  };

  const load = async () => {
    try {
      const r = await get<History>("fred/series", { id }, ctx.signal);
      if (!r.data.v.length) throw new Error(`No observations for ${id}`);
      data = r.data;
      ctx.panel.status(`FRED, St. Louis Fed · ${data.id}`, r.meta);
      draw(data);
    } catch (e) {
      if (ctx.signal.aborted || data) return;
      const unknown = e instanceof ApiError && e.status === 400;
      clear(head, errorBox(unknown ? `Unknown series ${id}. ECST <GO> lists the available ones.` : e));
    }
  };
  drawToolbar();
  load();
  ctx.panel.every(60 * 60_000, load);
}

register(
  {
    code: "GC",
    name: "Treasury Yield Curve",
    group: "Rates & Economy",
    needsSec: false,
    help: "US Treasury par curve vs 1D/1W/1M/1Y ago, changes in bp, 2s10s and other spread history.",
    aliases: ["YCRV", "CURVE"],
    run: runGC,
  },
  {
    code: "ECST",
    name: "Economic Statistics",
    group: "Rates & Economy",
    needsSec: false,
    help: "Headline US economic data from FRED: growth, jobs, inflation, rates, housing. ECST UNRATE for a chart.",
    aliases: ["FRED"],
    run: runECST,
  },
);
